const encode = value => encodeURIComponent(String(value))

const cacheEntries = new Map()
const pendingRequests = new Map()

const normalizeTags = tags => {
  if (!tags) return []
  return Array.isArray(tags) ? tags : [tags]
}

const hasAnyTag = (entryTags = [], tags = []) =>
  entryTags.some(tag => tags.includes(tag))

const appendParam = (params, key, value) => {
  if (value === undefined || value === null) return

  if (Array.isArray(value)) {
    value.forEach(item => appendParam(params, key, item))
    return
  }

  if (typeof value === "object") {
    params.push([key, JSON.stringify(value)])
    return
  }

  params.push([key, value])
}

export const createRequestCacheKey = (url, config = {}) => {
  const parsedUrl = new URL(String(url), "http://request-cache.local")
  const params = []

  parsedUrl.searchParams.forEach((value, key) => {
    appendParam(params, key, value)
  })

  Object.entries(config.params || {}).forEach(([key, value]) => {
    appendParam(params, key, value)
  })

  params.sort(([keyA, valueA], [keyB, valueB]) => {
    if (keyA === keyB) return String(valueA).localeCompare(String(valueB))
    return keyA.localeCompare(keyB)
  })

  const query = params
    .map(([key, value]) => `${encode(key)}=${encode(value)}`)
    .join("&")

  return `GET:${parsedUrl.pathname}${query ? `?${query}` : ""}`
}

export const clearRequestCache = () => {
  cacheEntries.clear()
  pendingRequests.clear()
}

export const invalidateCacheTags = tags => {
  const normalizedTags = normalizeTags(tags)

  if (!normalizedTags.length) return

  cacheEntries.forEach((entry, cacheKey) => {
    if (hasAnyTag(entry.tags, normalizedTags)) {
      cacheEntries.delete(cacheKey)
    }
  })

  pendingRequests.forEach((entry, cacheKey) => {
    if (hasAnyTag(entry.tags, normalizedTags)) {
      pendingRequests.delete(cacheKey)
    }
  })
}

export const cachedGet = async (
  url,
  { config = {}, ttlMs, fetcher, key, tags } = {}
) => {
  if (!fetcher) {
    throw new Error("cachedGet requires a fetcher")
  }

  if (!ttlMs) {
    return fetcher(url, config)
  }

  const cacheKey = key || createRequestCacheKey(url, config)
  const cacheTags = normalizeTags(tags)
  const now = Date.now()
  const existingEntry = cacheEntries.get(cacheKey)

  if (existingEntry && existingEntry.expiresAt > now) {
    return existingEntry.value
  }

  const pendingRequest = pendingRequests.get(cacheKey)
  if (pendingRequest) {
    return pendingRequest.promise
  }

  const requestEntry = {
    tags: cacheTags,
  }

  requestEntry.promise = Promise.resolve()
    .then(() => fetcher(url, config))
    .then(value => {
      if (pendingRequests.get(cacheKey) === requestEntry) {
        cacheEntries.set(cacheKey, {
          expiresAt: Date.now() + ttlMs,
          tags: cacheTags,
          value,
        })
      }

      return value
    })
    .finally(() => {
      if (pendingRequests.get(cacheKey) === requestEntry) {
        pendingRequests.delete(cacheKey)
      }
    })

  pendingRequests.set(cacheKey, requestEntry)

  return requestEntry.promise
}
